import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { grades, projects } from '../services/api';
import { nomUsuari, nomExecutor } from '../utils/usuari';

// Es poden qualificar tots els exercicis finalitzats. Fins al 07/08/2026 aquesta
// llista deixava fora 'abandoned', així que els 6 exercicis abandonats del curs no
// tenien botó de qualificar tot i tenir feina a dins. El servidor ja els accepta.
const QUALIFICABLES = ['submitted', 'time_expired', 'abandoned', 'graded'];

const ESTAT_ETIQUETA = {
  submitted: 'Enviat',
  graded: 'Qualificat',
  time_expired: 'Temps esgotat',
  abandoned: 'Abandonat',
  in_progress: 'En progrés',
  paused: 'Pausat',
  not_started: 'No iniciat'
};

const ESTAT_COLOR = {
  submitted: 'bg-warning-subtle text-warning-text',
  graded: 'bg-success-subtle text-success-text',
  time_expired: 'bg-warning-subtle text-warning-text',
  abandoned: 'bg-error-subtle text-error-text'
};


function GradingPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [assessmentGrades, setAssessmentGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [gradeForm, setGradeForm] = useState({ grade: '', feedback: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [projectRes, gradesRes] = await Promise.all([
        projects.getById(projectId),
        grades.getForProject(projectId)
      ]);
      setProject(projectRes.data);
      setAssessmentGrades(gradesRes.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant les dades');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGradeSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAssessment) return;

    try {
      setSubmitting(true);
      await grades.submit(selectedAssessment.assessmentId, {
        grade: parseInt(gradeForm.grade, 10),
        feedback: gradeForm.feedback
      });

      // Update local state
      setAssessmentGrades(prev =>
        prev.map(a =>
          a.assessmentId === selectedAssessment.assessmentId
            ? {
                ...a,
                status: 'graded',
                grade: {
                  grade: parseInt(gradeForm.grade, 10),
                  feedback: gradeForm.feedback,
                  gradedAt: new Date().toISOString()
                }
              }
            : a
        )
      );

      setSelectedAssessment(null);
      setGradeForm({ grade: '', feedback: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Error enviant la qualificació');
    } finally {
      setSubmitting(false);
    }
  };

  const openGradeModal = (assessment) => {
    setSelectedAssessment(assessment);
    setGradeForm({
      grade: assessment.grade?.grade?.toString() || '',
      feedback: assessment.grade?.feedback || ''
    });
  };

  // Group by case
  const groupedByCases = assessmentGrades.reduce((acc, a) => {
    const caseId = a.case.id;
    if (!acc[caseId]) {
      acc[caseId] = {
        case: a.case,
        assessments: []
      };
    }
    acc[caseId].assessments.push(a);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Link to={`/projects/${projectId}`} className="text-content-muted hover:text-content-secondary">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-2xl font-bold text-content">Qualificació: {project?.title}</h1>
      </div>

      {error && (
        <div className="bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="ml-2">×</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-content-muted">Total avaluacions</p>
          <p className="text-2xl font-bold text-content">{assessmentGrades.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-content-muted">Pendent de revisió</p>
          <p className="text-2xl font-bold text-warning">
            {assessmentGrades.filter(a => QUALIFICABLES.includes(a.status) && !a.grade).length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-content-muted">Qualificat</p>
          <p className="text-2xl font-bold text-success">
            {assessmentGrades.filter(a => a.status === 'graded').length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-content-muted">Nota mitjana</p>
          <p className="text-2xl font-bold text-content">
            {assessmentGrades.filter(a => a.grade).length > 0
              ? Math.round(
                  assessmentGrades
                    .filter(a => a.grade)
                    .reduce((sum, a) => sum + a.grade.grade, 0) /
                    assessmentGrades.filter(a => a.grade).length
                )
              : '-'}
          </p>
        </div>
      </div>

      {/* Assessments by case */}
      {Object.keys(groupedByCases).length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-content-muted">Encara no hi ha avaluacions per qualificar</p>
        </div>
      ) : (
        Object.values(groupedByCases).map(({ case: caseItem, assessments }) => (
          <div key={caseItem.id} className="card">
            <h2 className="text-lg font-semibold text-content mb-4">{caseItem.title}</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-content-muted border-b border-border">
                    <th className="pb-2">Estudiant/Equip</th>
                    <th className="pb-2">Estat</th>
                    <th className="pb-2">Enviat</th>
                    <th className="pb-2">Nota</th>
                    <th className="pb-2">Accions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {assessments.map((assessment) => (
                    <tr key={assessment.assessmentId} className="text-sm">
                      <td className="py-3 text-content">
                        {assessment.user ? (
                          <span>{nomUsuari(assessment.user)}</span>
                        ) : (
                          <span className="text-accent">{assessment.team?.name}</span>
                        )}
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          ESTAT_COLOR[assessment.status] || 'bg-surface-muted text-content'
                        }`}>
                          {ESTAT_ETIQUETA[assessment.status] || assessment.status}
                        </span>
                      </td>
                      <td className="py-3 text-content-muted">
                        {assessment.submittedAt
                          ? new Date(assessment.submittedAt).toLocaleDateString()
                          : assessment.endedAt
                          ? new Date(assessment.endedAt).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="py-3 text-content">
                        {assessment.grade ? (
                          <span className="font-semibold">{assessment.grade.grade}/100</span>
                        ) : (
                          <span className="text-content-disabled">-</span>
                        )}
                      </td>
                      <td className="py-3 space-x-2">
                        <Link
                          to={`/assessments/${assessment.assessmentId}`}
                          className="text-primary-600 hover:text-accent-hover"
                        >
                          Veure
                        </Link>
                        {QUALIFICABLES.includes(assessment.status) && (
                          <button
                            onClick={() => openGradeModal(assessment)}
                            className="text-green-600 hover:text-success-text"
                          >
                            {assessment.grade ? 'Editar nota' : 'Qualificar'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* Grade Modal */}
      {selectedAssessment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-content mb-4">
                Qualificar avaluació
              </h2>
              <p className="text-sm text-content-muted mb-4">
                {nomExecutor(selectedAssessment)}
              </p>
              <form onSubmit={handleGradeSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary">
                    Nota (0-100)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={gradeForm.grade}
                    onChange={(e) => setGradeForm({ ...gradeForm, grade: e.target.value })}
                    className="input mt-1"
                    placeholder="Introdueix la nota"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content-secondary">
                    Comentaris
                  </label>
                  <textarea
                    value={gradeForm.feedback}
                    onChange={(e) => setGradeForm({ ...gradeForm, feedback: e.target.value })}
                    className="input mt-1"
                    rows={4}
                    placeholder="Proporciona comentaris a l'estudiant..."
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setSelectedAssessment(null)}
                    className="btn btn-secondary"
                  >
                    Cancel·lar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {submitting ? 'Desant...' : 'Enviar nota'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GradingPage;
